import { readValue } from "../utils/storefrontApi";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from "@mui/material";
import axiosInstanceSynonyms from "../axiosInstanceSynonyms";
import StorefrontTaxonomyExclusions from "./StorefrontTaxonomyExclusions";
import TenantAutocomplete from "./TenantAutocomplete";


const readSearchFields = (data) => {
  const fields = readValue(data, "searchFields", "SearchFields");
  return Array.isArray(fields) ? fields.map((option) => ({
    field: readValue(option, "field", "Field"),
    label: readValue(option, "label", "Label"),
    enabled: readValue(option, "enabled", "Enabled"),
    canDisable: readValue(option, "canDisable", "CanDisable"),
  })) : null;
};

const intentFields = [
  ["sizeKeywords", "Size keywords", ["size"]],
  ["catalogueSizes", "Catalogue sizes", []],
  ["saleKeywords", "Sale keywords", ["on sale", "sale", "discounted", "on special"]],
  ["newArrivalPhrases", "New arrival phrases", ["new arrivals", "new arrival", "new in", "latest arrivals"]],
];
const readIntents = (data) => {
  const intents = readValue(data, "intents", "Intents");
  return { aroundBudgetTolerancePercent: readValue(intents, "aroundBudgetTolerancePercent", "AroundBudgetTolerancePercent") ?? 20,
    ...Object.fromEntries(intentFields.map(([key, , defaults]) => [key,
    (readValue(intents, key, key[0].toUpperCase() + key.slice(1)) ?? defaults).join("\n")
  ])) };
};

const SettingsEditor = ({ tenantId }) => {
  const endpoint = `/api/tenant/${encodeURIComponent(tenantId)}/storefront-settings`;
  const [configuration, setConfiguration] = useState(null);
  const [taxonomyIndexName, setTaxonomyIndexName] = useState("");
  const [searchFields, setSearchFields] = useState(null);
  const [categoryExclusions, setCategoryExclusions] = useState(null);
  const [intents, setIntents] = useState(null);
  const [intentsEdited, setIntentsEdited] = useState(false);
  const [cacheTtl, setCacheTtl] = useState(120);
  const [cacheTtlEdited, setCacheTtlEdited] = useState(false);
  const [fieldWeights, setFieldWeights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingSizes, setLoadingSizes] = useState(false);
  const sizeRequest = useRef(null);
  const [conflict, setConflict] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const mounted = useRef(false);

  const applyConfiguration = useCallback((data) => {
    setConfiguration(data);
    setCacheTtl(readValue(data, "firstPageCacheTtlSeconds", "FirstPageCacheTtlSeconds") ?? 120);
    setCacheTtlEdited(false);
    setIntents(readIntents(data));
    setIntentsEdited(false);
    setSearchFields(readSearchFields(data));
    setCategoryExclusions(readValue(data, "categoryExclusions", "CategoryExclusions") ?? null);
    setFieldWeights(readValue(data, "fieldWeights", "FieldWeights") ?? null);
    setTaxonomyIndexName(readValue(data, "taxonomyIndexName", "TaxonomyIndexName") || "");
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; sizeRequest.current?.abort(); };
  }, []);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setConfiguration(null);
    setCacheTtl(120);
    setCacheTtlEdited(false);
    setSearchFields(null);
    setCategoryExclusions(null);
    setFieldWeights(null);
    setIntents(null);
    setIntentsEdited(false);
    setFeedback(null);
    setConflict(false);
    axiosInstanceSynonyms.get(endpoint).then(({ data }) => {
      if (ignore) return;
      applyConfiguration(data);
    }).catch((error) => {
      if (ignore) return;
      if (error?.response?.status === 404) {
        setConfiguration({ revision: null });
        setIntents(readIntents(null));
        setTaxonomyIndexName("");
        setFeedback({ severity: "info", message: "No storefront settings were found. Save to create a configuration; this requires the storefront settings API." });
      } else {
        setFeedback({ severity: "error", message: "Could not load storefront settings. Reload to try again." });
      }
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [endpoint, refresh, applyConfiguration]);

  const populateCatalogueSizes = async () => {
    const controller = new AbortController();
    sizeRequest.current = controller;
    setLoadingSizes(true);
    setFeedback(null);
    try {
      const { data } = await axiosInstanceSynonyms.get(`/api/tenant/${encodeURIComponent(tenantId)}/storefront-catalogue-sizes`, { signal: controller.signal });
      if (!mounted.current || sizeRequest.current !== controller) return;
      const sizes = readValue(data, "sizes", "Sizes");
      if (!Array.isArray(sizes)) throw new Error("The catalog size response was invalid.");
      setIntents(current => ({ ...current, catalogueSizes: sizes.join("\n") }));
      setIntentsEdited(true);
      setFeedback({ severity: "info", message: sizes.length
        ? `Loaded ${sizes.length} catalogue sizes. Review the list and save settings to apply it.`
        : "No non-numeric catalogue sizes were found. The input is now empty; save settings to apply it." });
    } catch (error) {
      if (mounted.current && !controller.signal.aborted)
        setFeedback({ severity: "error", message: "Could not load catalogue sizes. Your existing input has been kept. Try again." });
    } finally {
      if (mounted.current && sizeRequest.current === controller) { setLoadingSizes(false); sizeRequest.current = null; }
    }
  };

  const invalidTolerance = intents && (String(intents.aroundBudgetTolerancePercent).trim() === ""
    || !Number.isFinite(Number(intents.aroundBudgetTolerancePercent))
    || Number(intents.aroundBudgetTolerancePercent) < 0 || Number(intents.aroundBudgetTolerancePercent) > 100);
  const invalidCacheTtl = String(cacheTtl).trim() === "" || !Number.isInteger(Number(cacheTtl))
    || Number(cacheTtl) < 0;
  const revision = readValue(configuration, "revision", "Revision");
  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await axiosInstanceSynonyms.put(endpoint, {
        expectedRevision: revision ?? null,
        ...(cacheTtlEdited ? { firstPageCacheTtlSeconds: Number(cacheTtl) } : {}),
        ...(intentsEdited ? { intents: { aroundBudgetTolerancePercent: Number(intents.aroundBudgetTolerancePercent), ...Object.fromEntries(intentFields.map(([key]) => [key, intents[key].split(/\r?\n/).map(item => item.trim()).filter(Boolean)])) } } : {}),
        taxonomyIndexName: taxonomyIndexName.trim() || null,
        ...(fieldWeights !== null ? { fieldWeights } : {}),
        ...(categoryExclusions !== null ? { categoryExclusions } : {}),
        ...(searchFields ? { searchFields: Object.fromEntries(searchFields.map(({ field, enabled, canDisable }) => [field, canDisable ? enabled : true])) } : {}),
      });
      if (!mounted.current) return;
      applyConfiguration(data);
      setFeedback({ severity: "success", message: "Storefront settings saved." });
    } catch (error) {
      if (!mounted.current) return;
      const changed = error?.response?.status === 409;
      setConflict(changed);
      setFeedback({ severity: "error", message: changed
        ? "Storefront settings changed since they were loaded. Reload before saving again."
        : (typeof error?.response?.data === "string" ? error.response.data : error?.response?.data?.message || error?.response?.data?.Message || error?.response?.data?.detail || error?.response?.data?.title || "Could not save storefront settings. Check access and that the storefront settings API is available.") });
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return <Stack spacing={2}>
    {feedback ? <Alert severity={feedback.severity}>{feedback.message}</Alert> : null}
    <Typography sx={{ fontWeight: 800 }}>First-page search cache</Typography>
    <TextField label="First-page cache TTL (seconds)" type="number" value={cacheTtl}
      inputProps={{ min: 0, step: 1 }}
      disabled={loading || saving || !configuration || conflict} error={invalidCacheTtl}
      helperText={invalidCacheTtl ? "Enter a non-negative whole number." : "Defaults to 120 seconds when unset. 0 disables caching. Matching searches reuse first-page ranking and facets for this duration; product details are fetched fresh. Changes to search settings, synonyms and merchandising rules invalidate cached results."}
      onChange={(event) => { setCacheTtl(event.target.value); setCacheTtlEdited(true); }} />
    <Typography sx={{ fontWeight: 800 }}>Search intents</Typography>
    {intents && <TextField label="Around budget tolerance (%)" type="number"
      value={intents.aroundBudgetTolerancePercent} inputProps={{ min: 0, max: 100, step: "any" }}
      disabled={loading || saving || !configuration || conflict} error={Boolean(invalidTolerance)}
      helperText={invalidTolerance ? "Enter a percentage between 0 and 100." : 'Percentage above and below the requested budget. For example, 20% makes “around 100” match 80–120.'}
      onChange={(event) => { setIntents(current => ({ ...current, aroundBudgetTolerancePercent: event.target.value })); setIntentsEdited(true); }} />}
    <Typography variant="body2" color="text.secondary">One value per line. Saved lists replace the defaults; an empty list stays empty. Catalogue sizes are this tenant’s non-numeric DisplaySize values. A size keyword must appear beside the value.</Typography>
    {intents && intentFields.map(([key, label]) => <Stack key={key} spacing={.5}>
      <TextField label={label} multiline minRows={2}
        value={intents[key]} disabled={loading || saving || !configuration || conflict || (key === "catalogueSizes" && loadingSizes)}
        onChange={(event) => { setIntents(current => ({ ...current, [key]: event.target.value })); setIntentsEdited(true); }} />
      {key === "catalogueSizes" && <Button sx={{ alignSelf: "flex-start" }} onClick={populateCatalogueSizes}
        disabled={loading || saving || loadingSizes || !configuration || conflict}>
        {loadingSizes ? "Loading catalogue sizes…" : "Populate from catalogue"}
      </Button>}
    </Stack>)}
    <Typography variant="body2" color="text.secondary">{loading ? "Loading storefront settings…" : `Revision: ${revision ?? "not created"}`}</Typography>
    <TextField label="Taxonomy index name" value={taxonomyIndexName} onChange={(event) => setTaxonomyIndexName(event.target.value)} disabled={loading || saving || !configuration || conflict} helperText="Search taxonomy index for this tenant. Leave blank to disable taxonomy category recognition." />
    <Box>
      <Typography sx={{ fontWeight: 800 }}>Search matching fields</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Choose which product fields match shopper queries in search and typeahead. Filters, facets, sorting and displayed product data are unaffected. Category is always enabled.</Typography>
      {searchFields ? <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } }}>
        {searchFields.map((option, index) => <FormControlLabel key={option.field}
          label={option.canDisable ? option.label : `${option.label} (always enabled)`}
          control={<Switch color="warning" checked={!option.canDisable || Boolean(option.enabled)}
            disabled={loading || saving || !configuration || conflict || !option.canDisable}
            onChange={(_, enabled) => setSearchFields((fields) => fields.map((field, fieldIndex) => fieldIndex === index ? { ...field, enabled } : field))} />} />)}
      </Box> : !loading ? <Typography variant="body2" color="text.secondary">Search field options are available after loading a configuration from the updated CMS API. New configurations use all fields by default.</Typography> : null}
    </Box>
    <Stack spacing={1}>
      <Typography sx={{ fontWeight: 800 }}>Search field priorities</Typography>
      <Typography variant="body2" color="text.secondary">For relevance sorting, the highest-priority field matching all search words wins: High, Medium, Low, then no matching priority. Existing relevance orders products within each level. Priorities apply to storefront search, not typeahead, price or newest sorting; pinned positions are preserved.</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
        {[["Title", "Title"], ["BodyHtml", "Description"], ["Tags", "Tags"]].map(([field, label]) => {
          const matchingDisabled = searchFields?.some((option) => option.field === field && !option.enabled);
          return <TextField key={field} select label={`${label} priority`} value={fieldWeights?.[field] || ""}
            disabled={loading || saving || !configuration || conflict} SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }} helperText={matchingDisabled ? "Matching is disabled for this field; its priority has no effect." : "No priority keeps ordinary relevance scoring."}
            onChange={(event) => {
              const value = event.target.value;
              setFieldWeights((current) => {
                const next = { ...current };
                if (value) next[field] = value; else delete next[field];
                return next;
              });
            }}>
            <option value="">No priority</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </TextField>;
        })}
      </Box>
      <Button sx={{ alignSelf: "flex-start" }} disabled={loading || saving || !configuration || conflict || !Object.keys(fieldWeights || {}).length} onClick={() => setFieldWeights({})}>Clear priorities</Button>
    </Stack>
    {configuration ? <StorefrontTaxonomyExclusions tenantId={tenantId} revision={revision}
      savedIndex={readValue(configuration, "taxonomyIndexName", "TaxonomyIndexName") || ""}
      indexChanged={taxonomyIndexName.trim() !== (readValue(configuration, "taxonomyIndexName", "TaxonomyIndexName") || "")}
      exclusions={categoryExclusions} onChange={setCategoryExclusions} disabled={loading || saving || conflict} /> : null}
    <Stack direction="row" spacing={1}>
      <Button variant="contained" onClick={save} disabled={loading || saving || loadingSizes || !configuration || conflict || invalidTolerance || invalidCacheTtl}>{saving ? "Saving…" : "Save settings"}</Button>
      <Button onClick={() => setRefresh((value) => value + 1)} disabled={loading || saving || loadingSizes}>Reload settings</Button>
    </Stack>
  </Stack>;
};

export default function StorefrontSettingsLab({ tenants = [], selectedTenant, setSelectedTenant }) {
  return <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: "18px" }}><Stack spacing={2}>
    <Typography sx={{ fontWeight: 850 }}>Storefront settings</Typography>
    <TenantAutocomplete tenants={tenants} value={tenants.find((tenant) => tenant.id === selectedTenant) || null} onChange={(_, tenant) => setSelectedTenant(tenant?.id || "")} label="Tenant" fullWidth size="small" />
    {selectedTenant ? <SettingsEditor key={selectedTenant} tenantId={selectedTenant} /> : <Alert severity="info">Select a tenant to manage its storefront settings.</Alert>}
  </Stack></Paper>;
}
